import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized } from 'vue-router';
import AuthHelper from '@/helpers/AuthHelper';
import BaseHelper from '@/helpers/BaseHelper';
import StoreHelper from '@/helpers/StoreHelper';
import config from '@/config/config';
import DbEnum from '@/enums/DbEnum';
const ClientRegistration = () =>
  import(/* webpackChunkName: "ClientRegistration" */ '@/views/contract/ClientRegistration.vue');
const Login = () => import(/* webpackChunkName: "Login" */ '@/views/login/Login.vue');
const Start = () => import(/* webpackChunkName: "Start" */ '@/views/login/Start.vue');
const PortalTop = () => import(/* webpackChunkName: "PortalTop" */ '@/views/top/PortalTop.vue');
const MyPage = () => import(/* webpackChunkName: "MyPage" */ '@/views/user/MyPage.vue');
const AsagakuList = () => import(/* webpackChunkName: "Asagaku" */ '@/views/asagaku/AsagakuList.vue');
const AsagakuDetail = () => import(/* webpackChunkName: "Asagaku" */ '@/views/asagaku/AsagakuDetail.vue');
const Oyakusoku = () => import(/* webpackChunkName: "Oyakusoku" */ '@/views/general/Oyakusoku.vue');
const Agreement = () => import(/* webpackChunkName: "Agreement" */ '@/views/general/Agreement.vue');
const TrialTerms = () => import(/* webpackChunkName: "TrialTerms" */ '@/views/general/TrialTerms.vue');
const AdminTrialTerms = () => import(/* webpackChunkName: "AdminTrialTerms" */ '@/views/general/AdminTrialTerms.vue');
const GigaSchoolGroupTerms = () =>
  import(/* webpackChunkName: "GigaSchoolGroupTerms" */ '@/views/general/GigaSchoolGroupTerms.vue');
const GigaSchoolAdminTerms = () =>
  import(/* webpackChunkName: "GigaSchoolAdminTerms" */ '@/views/general/GigaSchoolAdminTerms.vue');
const AboutAbj = () => import(/* webpackChunkName: "AboutAbj" */ '@/views/general/AboutAbj.vue');
const NotFoundError = () => import(/* webpackChunkName: "NotFoundError" */ '@/views/error/404.vue');
const ServerError = () => import(/* webpackChunkName: "ServerError" */ '@/views/error/500.vue');
const ServerMaintenance = () => import(/* webpackChunkName: "ServerMaintenance" */ '@/views/error/503.vue');
const InquiryForm = () => import(/* webpackChunkName: "InquiryForm" */ '@/views/general/InquiryForm.vue');
const LandingPage = () => import(/* webpackChunkName: "LandingPage" */ '@/views/general/LandingPage/LandingPage.vue');
const Ym = () => import(/* webpackChunkName: "LandingPage" */ '@/views/general/LandingPage/ym/Ym.vue');
const AdminAgreement = () => import(/* webpackChunkName: "AdminAgreement" */ '@/views/general/AdminAgreement.vue');
const SessionError = () => import(/* webpackChunkName: "SessionError" */ '@/views/error/SessionError.vue');
const Loading = () => import(/* webpackChunkName: "Loading" */ '@/views/login/Loading.vue');

enum ROUTE_TYPE {
  PUBLIC, // いつでも見れる画面
  NEED_AUTH, // (デフォルト) ログイン済みのユーザーしか見れない画面
  NEED_UNAUTH, // 未ログインのユーザーしか見れない画面
}

enum SCHEMA_TYPE {
  STRING,
  NUMBER,
  BOOLEAN,
}

interface ParamsSchema {
  [key: string]: SCHEMA_TYPE;
}

type PropType = string | number | boolean | null;

function castParamsToProps(route: RouteLocationNormalized, schema: ParamsSchema) {
  const props: { [x: string]: PropType | PropType[] } = {
    ...route.params,
    ...route.query,
  };
  const keys = Object.keys(props);
  keys.forEach(key => {
    if (schema[key]) {
      if (schema[key] === SCHEMA_TYPE.NUMBER) {
        props[key] = Number(props[key]);
      } else if (schema[key] === SCHEMA_TYPE.BOOLEAN) {
        props[key] = props[key] === 'true' ? true : false;
      }
    }
  });
  return props;
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    // errorType はパスに含めず query で受け取る（Vue Router 4 はパス未定義の params を破棄するため）
    props(route) {
      const schema = {
        errorType: SCHEMA_TYPE.STRING,
      };
      return castParamsToProps(route, schema);
    },
    meta: {
      routeType: ROUTE_TYPE.NEED_UNAUTH,
    },
  },
  {
    path: '/start',
    name: 'Start',
    // errorType/isLibrary/userId はパスに含めず query で受け取る（Vue Router 4 はパス未定義の params を破棄するため）
    props(route) {
      const schema = {
        errorType: SCHEMA_TYPE.STRING,
        isLibrary: SCHEMA_TYPE.STRING,
        userId: SCHEMA_TYPE.STRING,
      };
      return castParamsToProps(route, schema);
    },
    component: Start,
    meta: {
      routeType: ROUTE_TYPE.NEED_UNAUTH,
    },
  },
  {
    path: '/',
    name: 'PortalTop',
    component: PortalTop,
    meta: {
      routeType: ROUTE_TYPE.NEED_AUTH,
    },
  },
  {
    path: '/my_page',
    name: 'MyPage',
    component: MyPage,
    meta: {
      routeType: ROUTE_TYPE.NEED_AUTH,
    },
  },
  {
    path: '/asagaku_list',
    name: 'AsagakuList',
    component: AsagakuList,
    meta: {
      routeType: ROUTE_TYPE.NEED_AUTH,
    },
  },
  {
    path: '/asagaku_detail/:contentsPath',
    name: 'AsagakuDetail',
    component: AsagakuDetail,
    meta: {
      routeType: ROUTE_TYPE.NEED_AUTH,
    },
    props(route) {
      const schema = {
        contentsPath: SCHEMA_TYPE.STRING,
      };
      const props = castParamsToProps(route, schema);
      return props;
    },
  },
  {
    path: '/lp',
    name: 'LandingPage',
    component: LandingPage,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
    beforeEnter() {
      // リダイレクト
      window.location.replace('https://kodomottolab.poplar.co.jp/mottosokka');
    },
  },
  {
    path: '/lp/ym',
    name: 'Ym',
    component: Ym,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
    beforeEnter() {
      // リダイレクト
      window.location.replace('https://kodomottolab.poplar.co.jp/mottosokka/yomokka');
    },
  },
  {
    path: '/inquiry_form',
    name: 'InquiryForm',
    component: InquiryForm,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/oyakusoku',
    name: 'Oyakusoku',
    component: Oyakusoku,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/agreement',
    name: 'Agreement',
    component: Agreement,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/trial-terms',
    name: 'TrialTerms',
    component: TrialTerms,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/admin-trial-terms',
    name: 'AdminTrialTerms',
    component: AdminTrialTerms,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/giga-school-group-terms',
    name: 'GigaSchoolGroupTerms',
    component: GigaSchoolGroupTerms,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/giga-school-admin-terms',
    name: 'GigaSchoolAdminTerms',
    component: GigaSchoolAdminTerms,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/abj',
    name: 'AboutAbj',
    component: AboutAbj,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/404',
    name: 'NotFoundError',
    component: NotFoundError,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/500',
    name: 'ServerError',
    component: ServerError,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/503',
    name: 'ServerMaintenance',
    component: ServerMaintenance,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/client_registration',
    name: 'ClientRegistration',
    component: ClientRegistration,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/library',
    name: 'library',
    redirect: '/',
  },
  {
    path: '/book_search',
    name: 'book_search',
    redirect: '/',
  },
  {
    path: '/book_ranking',
    name: 'book_ranking',
    redirect: '/',
  },
  {
    path: '/bookshelf',
    name: 'bookshelf',
    redirect: '/',
  },
  {
    path: '/book_detail/:bookId',
    name: 'book_detail',
    redirect: '/',
  },
  {
    path: '/admin_agreement',
    name: 'AdminAgreement',
    component: AdminAgreement,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
  },
  {
    path: '/session_error/:errorType',
    name: 'SessionError',
    component: SessionError,
    meta: {
      routeType: ROUTE_TYPE.PUBLIC,
    },
    props(route) {
      const schema = {
        errorType: SCHEMA_TYPE.STRING,
      };
      const props = castParamsToProps(route, schema);
      return props;
    },
  },
  {
    path: '/loading/:idpType',
    name: 'Loading',
    component: Loading,
    meta: {
      routeType: ROUTE_TYPE.NEED_UNAUTH,
    },
    props(route) {
      const schema = {
        idpType: SCHEMA_TYPE.NUMBER,
      };
      const props = castParamsToProps(route, schema);
      return props;
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return { el: to.hash };
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return { left: 0, top: 0 };
    }
  },
});

function isIdleForTooLong() {
  if (StoreHelper.getIdleTime() > config.allowedIdleMinutes) {
    return true;
  }
  return false;
}

// eslint-disable-next-line max-lines-per-function, complexity
router.beforeEach(async (to, from, next) => {
  const routeType = (to.meta?.routeType as ROUTE_TYPE | undefined) ?? ROUTE_TYPE.NEED_AUTH;
  if (routeType === ROUTE_TYPE.PUBLIC) {
    try {
      await StoreHelper.getAndSetUser();
    } finally {
      // PUBLIC画面の場合、直接移動します。
      next();
    }
    return;
  }

  let user;
  try {
    // ログイン中の場合、ユーザーを取得する
    user = await StoreHelper.getAndSetUser();
  } catch (err) {
    const location = await BaseHelper.handleErrors(null, err as Error);
    next(location);
    return;
  }

  // 未ログインで未ログインのユーザーしか見れない画面に遷移する場合
  if (
    !user &&
    routeType === ROUTE_TYPE.NEED_UNAUTH &&
    (to.name !== 'Start' || (to.name === 'Start' && Object.keys(to.query).length !== 0))
  ) {
    next();
    return;
  }

  let isAllowIp = null;
  try {
    if (!user) {
      isAllowIp = await AuthHelper.ipCheck();
    }
  } catch (err) {
    const location = await BaseHelper.handleErrors(null, err as Error);
    next(location);
    return;
  }

  // ログイン状態で60分以上画面操作が行われていない場合
  if (isIdleForTooLong()) {
    AuthHelper.logout();
    StoreHelper.updateLastUseTime();
    if (isAllowIp && isAllowIp.isFixedIp) {
      next({
        name: 'Start',
        query: {
          errorType: 'auto',
          isLibrary: String(isAllowIp.constraintOption!),
          userId: String(isAllowIp.userId!),
        },
      });
    } else {
      next({ name: 'Login', query: { errorType: 'auto' } });
    }
    return;
  }

  // ログインしている状態で公共図書館ユーザーがポータルトップ画面に入ろうとしたら、Sagasokka!トップ画面に遷移するにリダイレクト
  if (user && user.constraintOption === DbEnum.constraintOption.library.value && to.name === 'PortalTop') {
    window.location.href = config.sagasokkaUrl;
    return;
  }

  // ログインしている状態でログイン画面に入ろうとしたら、ポータルトップにリダイレクト
  if (user && routeType === ROUTE_TYPE.NEED_UNAUTH) {
    next({ name: 'PortalTop' });
    return;
  }

  // ログインしていない場合、ログイン画面にリダイレクト（固定IPじゃない場合）
  if (!user && isAllowIp && !isAllowIp.isFixedIp && routeType === ROUTE_TYPE.NEED_AUTH) {
    next({ name: 'Login' });
    return;
  }

  // ログインしていない場合、スタート画面にリダイレクト（固定IPの場合）
  if (!user && isAllowIp && isAllowIp.isFixedIp && (routeType === ROUTE_TYPE.NEED_AUTH || to.name === 'Start')) {
    next({
      name: 'Start',
      query: { isLibrary: String(isAllowIp.constraintOption!), userId: String(isAllowIp.userId!) },
    });
    return;
  }

  // 申込管理者かつ、パスワード変更フラグがfalseだった場合
  if (user?.contractApplicationManagerFlg === 1 && user.passwordConfigured === 0 && to.name !== 'PortalTop') {
    next({ name: 'PortalTop' });
    return;
  }

  next();
  return;
});

export default router;
